import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import TranslatedContent from '../TranslatedContent';
import { useLanguage } from '../../context/LanguageContext';

// Auto-rotating hero banner with CMS-driven slides + a fallback hero when none configured.
const HeroSection = ({ heroes }) => {
  const { t } = useLanguage();
  const [currentHero, setCurrentHero] = useState(0);

  useEffect(() => {
    if (heroes.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentHero((prev) => {
        const next = (prev + 1) % heroes.length;
        // Preload upcoming image to avoid jank
        const img = new Image();
        img.src = heroes[next]?.image_url;
        return next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [heroes]);

  if (heroes.length === 0) {
    return (
      <section className="themed-hero relative h-[600px] overflow-hidden">
        <div className="relative w-full h-full">
          <img
            src="https://images.unsplash.com/photo-1608569569089-5d2e3e644ea6"
            alt="Welcome"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl fade-in">
                <h1 className="responsive-hero-title font-serif font-bold text-white mb-4">
                  {t('welcome')} <br />Vår Fru av Fatima
                </h1>
                <p className="text-xl text-gray-200 mb-4">Katolska Församlingen i Blekinge</p>
                <p className="text-lg text-gray-300 mb-8">{t('heroSubtitle')}</p>
                <Link to="/contact">
                  <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                    {t('contactUs')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const hero = heroes[currentHero];

  return (
    <section className="themed-hero relative h-[600px] overflow-hidden">
      <div className="relative w-full h-full">
        {heroes.length > 1 && (
          <link
            rel="preload"
            as="image"
            href={heroes[(currentHero + 1) % heroes.length]?.image_url}
          />
        )}
        <img src={hero?.image_url} alt="Hero" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl fade-in">
              <TranslatedContent
                content={hero?.title || { sv: '' }}
                as="h1"
                className="responsive-hero-title font-serif font-bold text-white mb-4"
              />
              <TranslatedContent
                content={hero?.subtitle || { sv: '' }}
                as="p"
                className="text-xl text-gray-200 mb-8"
              />
              {hero?.cta_text && (
                <Link to={hero?.cta_link || '/contact'}>
                  <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                    <TranslatedContent content={hero?.cta_text || { sv: '' }} as="span" />
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
        {heroes.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {heroes.map((h, idx) => (
              <button
                key={`hero-indicator-${h.id || idx}`}
                onClick={() => setCurrentHero(idx)}
                className={`w-3 h-3 rounded-full transition-all ${idx === currentHero ? 'bg-yellow-500 w-8' : 'bg-white/50'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection;
